import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import gql from "graphql-tag"
import TaskManagementListHeader from './TaskManagementListHeader'
import TaskList from './TaskList'
import MyTasksGroupedList from './MyTasksGroupedList'
import TaskKanbanView from './TaskKanbanView'
import { AllColumnsQuery } from './Queries'
import withApolloClient from '../withApolloClient'
import { UserQuery } from '../Functions/querys'
import { useAdvancedFilters, advancedFiltersWereTouched } from '../Filters/filterUrl'
import { combineFilters, buildPresets } from '../Filters/presetFilters'
import { getTaskFilterPresets, stripGroupedFilterForKanban } from './taskFilterPresets'
import { taskFilterBoardKey, readSavedTaskFilters, writeSavedTaskFilters } from './taskFilterStorage'

const UpdateMemberMutation = gql`
    mutation updateMember($input: UpdateMemberMutationInput!) {
        updateMember(input: $input) {
            ok
            errors {
                messages
                field
            }
        }
    }
`

// Stable identity: an inline [] would be a new array every render and churn the
// memos and callbacks that depend on it.
const EMPTY_PRESETS = []

// Presets carry ui metadata (id/editable/visible) the URL has no business storing.
const toFilterInput = ({ fieldName, filterType, filterValues }) => ({ fieldName, filterType, filterValues })

// Order is meaningful here rather than incidental: both sides are built from the
// same defaults in the same order, so a positional compare is what we want, and
// it correctly reports "changed" when the user reorders.
const filtersEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b)

function TaskManagementViewContainer({ apolloClient, defaultColumnConfig, tablePreferenceId, currentUser }) {
    const { boardId } = useParams()
    const location = useLocation()
    const { data: userData, loading: userPrefsLoading } = useQuery(UserQuery, { fetchPolicy: 'cache-first' })

    // Custom columns are fetched here rather than nested in the sidebar's board query --
    // only the board being viewed needs them. "My Tasks" has no board, so no custom columns.
    const isBoardView = Boolean(boardId) && boardId !== 'tasks'
    const { data: boardColumnsData } = useQuery(AllColumnsQuery, {
        variables: { boardId },
        skip: !isBoardView,
        fetchPolicy: 'cache-and-network',
    })

    // Appended to the base config once they arrive. TableColumnPreferenceControl watches
    // this value and re-applies the user's saved visibility/order on top of it, so a late
    // arrival doesn't lose their preferences.
    const columnConfigWithCustomColumns = useMemo(() => {
        const baseConfig = defaultColumnConfig || []
        if (!isBoardView) return baseConfig
        const customColumns = (boardColumnsData?.allColumns?.edges || [])
            .filter(({ node }) => !node.isArchived)
            .map(({ node }) => ({
                id: `custom_column_${node.pk}`,
                name: node.title,
                sortable: false,
                columnNode: node,
                isCustom: true,
                preferenceSettings: {
                    isVisible: true,
                    canToggleVisibility: true,
                    canReorder: true,
                },
            }))
        return customColumns.length ? [...baseConfig, ...customColumns] : baseConfig
    }, [defaultColumnConfig, boardColumnsData, isBoardView])
    const memberPrefs = userData?.currentUser?.member
    const memberPrefsRef = useRef(memberPrefs)
    const [columnConfig, setColumnConfig] = useState(columnConfigWithCustomColumns)
    // The table whose saved preference has landed -- deliberately the id, not a
    // boolean. A boolean has to be reset when the table changes, and that reset
    // lived in the effect below, which runs AFTER the header's publish in the same
    // commit (children flush before parents). Whenever the header had nothing to
    // await it published synchronously -- a user with no `member` record, on a warm
    // CURRENT_USER_QUERY cache -- and the reset discarded it. Neither side repeats
    // itself, so My Tasks then spun forever. Comparing ids makes a table change
    // invalidate hydration on its own, with no write to be clobbered.
    const [columnConfigHydratedFor, setColumnConfigHydratedFor] = useState(null)
    const columnConfigHydrated = columnConfigHydratedFor === tablePreferenceId
    const lastTablePreferenceIdRef = useRef(null)

    // columnConfig is a query variable on My Tasks now -- isRelatedObjectsColumnVisible
    // feeds includeRelatedObjects, which is what switches the server-side FK prefetch
    // on. The seeded config has Related Objects visible, but no existing user's saved
    // preference mentions the column (it predates them), so it lands hidden the moment
    // the preference resolves. Rendering the list before then would fetch every group
    // with the wrong variables and refetch on arrival, flashing the column in and out.
    // TableColumnPreferenceControl calls this exactly once the saved preference has
    // resolved, or once it knows there is none to load.
    const handleSetColumnConfig = useCallback((nextColumnConfig) => {
        setColumnConfigHydratedFor(tablePreferenceId)
        setColumnConfig(nextColumnConfig)
    }, [tablePreferenceId])

    const [orderBy, setOrderBy] = useState(['dueDate'])
    const [groupBy, setGroupBy] = useState('status')
    const [pendingViewType, setPendingViewType] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [advancedFilters, setAdvancedFilters] = useAdvancedFilters()

    // The page's default filters. Deliberately derived, not useState: the moment
    // the user changes any of them the whole visible set moves into ?filter=
    // (see below), so there is no edited-preset state left to hold.
    const defaultPresets = useMemo(
        () => buildPresets(getTaskFilterPresets(boardId, currentUser.id)),
        [boardId, currentUser.id],
    )
    // for_user carries visible:false. It is page policy rather than a filter --
    // applied to every query, never written to the URL, so a shared link cannot
    // carry one person's identity onto somebody else's page.
    const pagePolicyPresets = useMemo(() => defaultPresets.filter((p) => p.visible === false), [defaultPresets])
    const visibleDefaults = useMemo(() => defaultPresets.filter((p) => p.visible !== false), [defaultPresets])

    // ?filter= stays empty while the visible filters match the defaults. As soon
    // as anything differs it carries the COMPLETE visible set -- edited defaults
    // included -- which is what lets a *removed* default stay removed across a
    // reload. An absent field in a written ?filter= means "deleted", not
    // "untouched", and that distinction is impossible if defaults stay implicit.
    const filtersAreCustomized = (advancedFilters || []).length > 0
    const visibleFilters = filtersAreCustomized ? advancedFilters : visibleDefaults
    // Once customized, every chip and popup row comes from the URL, so the page
    // offers no presets of its own -- otherwise the defaults would render twice.
    const uiPresets = filtersAreCustomized ? EMPTY_PRESETS : visibleDefaults
    const boardKey = taskFilterBoardKey(boardId)

    // The board key hydration has finished for. Deliberately state, not a ref:
    // this component stays mounted across board switches, so the save effect
    // below re-runs in the same commit as the boardId change -- still closed
    // over the PREVIOUS board's filters. A ref would already read as "hydrated
    // for the new board" at that point (the hydration effect runs first in the
    // same flush and refs are written synchronously) and the stale value would
    // be persisted into the new board's slot, wiping it. A state value is only
    // visible on the next render, by which time advancedFilters has caught up.
    const [hydratedKey, setHydratedKey] = useState(null)
    const filterHydrated = hydratedKey === boardKey
    // Guards "hydrate once per board" without waiting for the state above.
    const hydrationStartedKeyRef = useRef(null)
    // The filters last known to be on the server for the current board, as JSON.
    // Seeded at hydration so the value hydration itself produced is never
    // written back: hydrating from the saved entry would be a no-op write, and
    // hydrating from a shared ?filter= link would overwrite the recipient's own
    // saved filters with the sender's. Only post-hydration changes persist.
    const lastPersistedRef = useRef(null)

    const getPersistedViewType = () => {
        try {
            const config = JSON.parse(memberPrefs?.boardViewTypeConfig || '{}')
            return config[boardKey] || memberPrefs?.taskManagementViewType || "list"
        } catch (e) {
            return memberPrefs?.taskManagementViewType || "list"
        }
    }

    const viewType = pendingViewType !== null ? pendingViewType : getPersistedViewType()

    useEffect(() => { memberPrefsRef.current = memberPrefs }, [memberPrefs])

    useEffect(() => {
        setPendingViewType(null)
        // Filter hydration is not reset here -- it is keyed on the board
        // (hydratedKey / hydrationStartedKeyRef), so a board switch invalidates
        // it on its own without an extra state write.
        //
        // Advanced filters aren't reset here either -- useAdvancedFilters reads
        // straight from the URL, and board switches navigate to a new path
        // (dropping any old query string) on their own.
    }, [boardId])

    useEffect(() => {
        if (!memberPrefs?.kanbanColumnConfig) return
        try {
            const config = JSON.parse(memberPrefs.kanbanColumnConfig)
            setGroupBy(boardId && config[boardId] ? config[boardId] : 'status')
        } catch (e) { }
    }, [memberPrefs?.kanbanColumnConfig, boardId])

    // Saved filters hydrate only when the URL carries none -- a shared ?filter=
    // link must show the sender's filters, not the recipient's saved ones.
    useEffect(() => {
        if (!memberPrefs || hydrationStartedKeyRef.current === boardKey) return
        hydrationStartedKeyRef.current = boardKey
        let filtersInForce = advancedFilters || []
        if (!filtersInForce.length) {
            const saved = readSavedTaskFilters(memberPrefs.taskFilterConfig, boardKey)
            if (saved && saved.length) {
                setAdvancedFilters(saved)
                filtersInForce = saved
            }
        }
        lastPersistedRef.current = JSON.stringify(filtersInForce)
        setHydratedKey(boardKey)
        // advancedFilters is read once at hydration time on purpose; adding it to the
        // deps would re-run this on every filter change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberPrefs, boardKey, setAdvancedFilters])

    // Keyed on the board only. Custom columns land asynchronously now, but re-seeding on
    // their arrival would overwrite the preferences TableColumnPreferenceControl restores
    // first (child effects run before parent effects) -- it already handles that case.
    useEffect(() => {
        if (lastTablePreferenceIdRef.current === tablePreferenceId) return
        lastTablePreferenceIdRef.current = tablePreferenceId
        setColumnConfig(columnConfigWithCustomColumns)
        // Hydration is NOT reset here. It is keyed on tablePreferenceId
        // (columnConfigHydratedFor above), so a table change invalidates it by
        // comparison alone. Writing it here would discard a publish the header
        // made in this same commit.
    }, [columnConfigWithCustomColumns, tablePreferenceId])

    const handleGroupByChange = async (newGroupBy) => {
        setGroupBy(newGroupBy)
        if (!boardId) return
        try {
            let existing = {}
            if (memberPrefs?.kanbanColumnConfig) {
                try { existing = JSON.parse(memberPrefs.kanbanColumnConfig) } catch (e) { }
            }
            await apolloClient.mutate({
                mutation: UpdateMemberMutation,
                variables: { input: { memberId: memberPrefs?.id, kanbanColumnConfig: JSON.stringify({ ...existing, [boardId]: newGroupBy }) } },
                refetchQueries: ['UserQuery'],
            })
        } catch (e) { }
    }

    const handleViewTypeChange = async (newViewType) => {
        setPendingViewType(newViewType)
        try {
            let existing = {}
            if (memberPrefs?.boardViewTypeConfig) {
                try { existing = JSON.parse(memberPrefs.boardViewTypeConfig) } catch (e) { }
            }
            await apolloClient.mutate({
                mutation: UpdateMemberMutation,
                variables: {
                    input: {
                        memberId: memberPrefs?.id,
                        boardViewTypeConfig: JSON.stringify({ ...existing, [boardKey]: newViewType }),
                    }
                },
                refetchQueries: ["UserQuery"],
            })
        } catch (error) {
            console.error("Error saving view type:", error)
        }
    }

    // Persist the user's filters per board. Presets are deliberately excluded --
    // they are page-owned config, re-applied from getTaskFilterPresets on load.
    //
    // Only changes made AFTER hydration are written. Persisting anything else
    // is data loss, not a redundant write: on a board switch this effect runs
    // before advancedFilters has caught up with the new board, and on a shared
    // ?filter= link the value in force is the sender's, not the recipient's.
    useEffect(() => {
        if (hydratedKey !== boardKey) return
        // Only a change the user actually made through the filter UI persists.
        // Every in-app board link is built without a search string, so following
        // one -- including clicking the board you are already on -- drops
        // `?filter=` and is indistinguishable from clearing every filter by hand.
        // Without this the saved filters were wiped by ordinary navigation.
        if (!advancedFiltersWereTouched(location)) return
        const nextFilters = advancedFilters || []
        const nextFiltersJson = JSON.stringify(nextFilters)
        if (nextFiltersJson === lastPersistedRef.current) return
        const prefs = memberPrefsRef.current
        if (!prefs?.id) return
        lastPersistedRef.current = nextFiltersJson
        const nextConfig = writeSavedTaskFilters(prefs.taskFilterConfig, boardKey, nextFilters)
        if (!nextConfig) return
        apolloClient.mutate({
            mutation: UpdateMemberMutation,
            variables: { input: { memberId: prefs.id, taskFilterConfig: nextConfig } },
            refetchQueries: ['UserQuery'],
        }).catch((e) => console.error("Error saving filter preference:", e))
    }, [advancedFilters, hydratedKey, boardKey, apolloClient, location])

    // A fresh object here would invalidate the header's debounced search
    // callback (useCallback(debounce(...), [filterValues])) on every parent
    // render, churning a timer per render.
    const headerFilterValues = useMemo(() => ({ searchTerm }), [searchTerm])

    const queryFilters = useMemo(
        // visibleFilters is either URL filters (already clean) or the defaults,
        // which still carry id/editable/visible -- combineFilters only strips
        // metadata from its first argument, so map before handing them over.
        () => stripGroupedFilterForKanban(
            combineFilters(pagePolicyPresets, visibleFilters.map(toFilterInput)),
            viewType,
            groupBy,
        ),
        [pagePolicyPresets, visibleFilters, viewType, groupBy]
    )

    // Writes the complete visible set, or clears ?filter= when it has come back
    // to matching the defaults -- so an untouched page keeps a clean URL, and so
    // does one the user has undone their changes on.
    const writeVisibleFilters = useCallback((nextPresets, userFilters) => {
        const next = [...(nextPresets || []).map(toFilterInput), ...(userFilters || [])]
        setAdvancedFilters(filtersEqual(next, visibleDefaults.map(toFilterInput)) ? [] : next)
    }, [setAdvancedFilters, visibleDefaults])

    // AdvancedFilters' Apply calls onPresetsChange and then onApply synchronously,
    // and each one alone knows only half the answer. Rather than navigate twice,
    // the preset half parks its value and defers: if Apply consumes it in the same
    // tick, this microtask sees the ref cleared and does nothing. A preset chip's
    // x, which fires on its own, still lands its write here.
    const pendingPresetsRef = useRef(null)
    const handlePresetsChange = useCallback((nextPresets) => {
        pendingPresetsRef.current = nextPresets
        Promise.resolve().then(() => {
            if (pendingPresetsRef.current !== nextPresets) return
            pendingPresetsRef.current = null
            writeVisibleFilters(nextPresets, filtersAreCustomized ? advancedFilters : [])
        })
    }, [writeVisibleFilters, filtersAreCustomized, advancedFilters])

    const handleApplyFilters = useCallback((userFilters) => {
        const nextPresets = pendingPresetsRef.current ?? uiPresets
        pendingPresetsRef.current = null
        writeVisibleFilters(nextPresets, userFilters)
    }, [writeVisibleFilters, uiPresets])

    const updateOrderBy = (string) => {
        let order = [...orderBy]
        if (!order.includes(string) && !order.includes("-" + string)) {
            order = [string]
        } else {
            let index = order.indexOf(string)
            if (order.indexOf(string) > -1) {
                order[index] = "-" + string
            } else {
                index = order.indexOf("-" + string)
                index > -1 && (order[index] = string)
            }
        }
        setOrderBy(order)
    }

    return (
        <>
            <TaskManagementListHeader
                filterValues={headerFilterValues}
                setFilterValues={(next) => setSearchTerm(next?.searchTerm ?? '')}
                presets={uiPresets}
                onPresetsChange={handlePresetsChange}
                onApplyFilters={handleApplyFilters}
                showNewBoardButton
                viewType={viewType}
                onViewTypeChange={handleViewTypeChange}
                groupBy={groupBy}
                onGroupByChange={handleGroupByChange}
                tableId={tablePreferenceId}
                defaultColumnConfig={columnConfigWithCustomColumns}
                columnConfig={columnConfig}
                setColumnConfig={handleSetColumnConfig}
                currentUser={currentUser}
            />

            {(userPrefsLoading && !memberPrefs) || !filterHydrated ? (
                <div className="text-center mt-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
            ) : viewType === "kanban" ? (
                boardId ? (
                    <TaskKanbanView boardId={boardId} searchTerm={searchTerm} orderBy={orderBy} groupBy={groupBy} filters={queryFilters} />
                ) : (
                    <TaskKanbanView searchTerm={searchTerm} orderBy={orderBy} groupBy={groupBy} filters={queryFilters} />
                )
            ) : (
                boardId ? (
                    <TaskList boardId={boardId} searchTerm={searchTerm} orderBy={orderBy} updateOrderBy={updateOrderBy} columnConfig={columnConfig} filters={queryFilters} />
                ) : !columnConfigHydrated ? (
                    // My Tasks only: the Related Objects column's visibility is part of
                    // this list's query variables, so the six group queries wait for the
                    // saved preference rather than firing twice. Board views are gated on
                    // filterHydrated alone, exactly as before -- their column config never
                    // reaches a query variable.
                    <div className="text-center mt-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
                ) : (
                    <MyTasksGroupedList searchTerm={searchTerm} orderBy={orderBy} updateOrderBy={updateOrderBy} columnConfig={columnConfig} filters={queryFilters} />
                )
            )}
        </>
    )
}

export default withApolloClient(TaskManagementViewContainer)
